import { useState } from 'react';
import BoardThumb from './BoardThumb';
import type { OpeningTheory } from '../../../shared/types';

/**
 * The strategic writeup for an opening — the "why" the whole app exists for.
 *
 * It is deliberately long-form rather than bullet-summarized: understanding a
 * pawn structure is what lets you find a move nobody wrote down, which is the
 * point of learning theory rather than memorizing a move list. The sections are
 * collapsible so it can also be skimmed once you know it.
 */

function Section({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`theory-section${open ? ' theory-section--open' : ''}`}>
      <button type="button" className="theory-toggle" onClick={() => setOpen((o) => !o)}>
        <span className="theory-toggle-caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        {title}
      </button>
      {open && <div className="theory-body">{children}</div>}
    </section>
  );
}

export default function TheoryPanel({
  theory,
  /** The opening's own position, so the writeup has a board to point at. */
  position,
}: {
  theory: OpeningTheory;
  position?: { fen: string; side: 'white' | 'black'; moves: string };
}) {
  return (
    <div className="theory">
      {/* Reading "the knight belongs on f5" while looking at the starting
          position is guesswork. The diagram is the position the whole writeup
          is actually about, set up and waiting. */}
      {position && (
        <div className="theory-position">
          <BoardThumb fen={position.fen} orientation={position.side} size={150} />
          <div className="theory-position-text">
            <span className="theory-position-label">The opening</span>
            <code className="opening-moves">{position.moves}</code>
            <p className="muted">Every variation starts from here.</p>
          </div>
        </div>
      )}

      <Section title="The idea" defaultOpen>
        <p>{theory.idea}</p>
      </Section>

      <Section title="Pawn structure" defaultOpen>
        <p>{theory.pawnStructure}</p>
        {theory.keySquares.length > 0 && (
          <p className="theory-squares">
            <span className="theory-squares-label">Key squares</span>
            {theory.keySquares.map((square) => (
              <code key={square} className="square-chip">
                {square}
              </code>
            ))}
          </p>
        )}
      </Section>

      <Section title="Where the pieces belong">
        <p>{theory.pieces}</p>
      </Section>

      <Section title="Middlegame plans" defaultOpen>
        <div className="plans">
          <div className="plans-col">
            <h4 className="plans-heading plans-heading--white">White is playing for</h4>
            <ul>
              {theory.plans.white.map((plan, i) => (
                <li key={i}>{plan}</li>
              ))}
            </ul>
          </div>
          <div className="plans-col">
            <h4 className="plans-heading plans-heading--black">Black is playing for</h4>
            <ul>
              {theory.plans.black.map((plan, i) => (
                <li key={i}>{plan}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section title="What people get wrong">
        <ul className="mistakes">
          {theory.typicalMistakes.map((mistake, i) => (
            <li key={i}>{mistake}</li>
          ))}
        </ul>
      </Section>

      <Section title="When to play it">
        <p>{theory.whenToPlay}</p>
        {theory.transpositions && theory.transpositions.length > 0 && (
          <>
            <h4 className="theory-subheading">Transpositions worth knowing</h4>
            <ul>
              {theory.transpositions.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </>
        )}
      </Section>
    </div>
  );
}
